import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useReviewDialog } from '../../hooks';
import { MapLayerSwitch, MapLayerButtons } from '../../components/map/MapLayerControls';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Send, ArrowLeft, Phone, MapPin, Clock, Truck, User,
  Loader2, CheckCircle, Navigation, MessageSquare, Star,
  ChevronRight, ChevronDown, ChevronUp, Route, CornerUpRight,
  CornerUpLeft, ArrowUp, RotateCcw, Flag, List
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { ReviewDialog, ReviewPrompt } from './components/ReviewComponents';

// Fix leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const customerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const providerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Status colors and labels
const STATUS_CONFIG = {
  pending: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'Waiting for Provider' },
  awaiting_payment: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'Payment Required' },
  awaiting_provider_payment: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'Provider Confirming' },
  accepted: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Provider Accepted' },
  en_route: { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', label: 'Provider En Route' },
  arrived: { color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', label: 'Provider Arrived' },
  in_progress: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'Service In Progress' },
  completed: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Completed' },
  cancelled: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Cancelled' },
};

// Map auto-center component
const MapController = ({ customerLocation, providerLocation }) => {
  const map = useMap();
  
  useEffect(() => {
    if (customerLocation && providerLocation) {
      const bounds = L.latLngBounds([
        [customerLocation.lat, customerLocation.lng],
        [providerLocation.lat, providerLocation.lng]
      ]);
      map.fitBounds(bounds, { padding: [50, 50] });
    } else if (customerLocation) {
      map.setView([customerLocation.lat, customerLocation.lng], 14);
    }
  }, [customerLocation, providerLocation, map]);
  
  return null;
};

export default function RoadsideTrackingPage() {
  const { requestId } = useParams();
  const { api, user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [providerLocation, setProviderLocation] = useState(null);
  const [eta, setEta] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [navigationSteps, setNavigationSteps] = useState([]);
  const [showDirections, setShowDirections] = useState(false);
  const [mapLayer, setMapLayer] = useState('dark');
  
  // Use the review dialog hook
  const { 
    showReviewDialog, 
    setShowReviewDialog, 
    hasReviewed,
    updateReviewStatus,
    handleReviewSubmitted 
  } = useReviewDialog();
  
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);

  // Fetch request details and messages
  useEffect(() => {
    fetchRequest();
    fetchMessages();
    
    // Poll for updates every 3 seconds
    const interval = setInterval(() => {
      fetchRequest();
      fetchMessages();
      fetchProviderLocation();
      fetchEta();
    }, 3000);
    
    return () => clearInterval(interval);
  }, [requestId]);

  // Fetch provider location and ETA when request data is loaded
  useEffect(() => {
    if (request?.provider_id) {
      fetchProviderLocation();
      fetchEta();
    }
  }, [request?.provider_id]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchRequest = async () => {
    try {
      const res = await api.get(`/roadside/requests/${requestId}`);
      setRequest(res.data);
      // Update hasReviewed state from API response using hook
      if (res.data?.has_review) {
        updateReviewStatus(true);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch request:', error);
      if (error.response?.status === 404) {
        toast.error('Request not found');
        navigate('/roadside');
      }
    }
  };

  const fetchEta = async () => {
    try {
      const res = await api.get(`/roadside/requests/${requestId}/eta`);
      if (res.data?.eta_available) {
        setEta(res.data);
        // Store route coordinates for map polyline
        if (res.data.route_coordinates && res.data.route_coordinates.length > 0) {
          setRouteCoordinates(res.data.route_coordinates);
        }
        // Store navigation steps if available
        if (res.data.navigation_steps && res.data.navigation_steps.length > 0) {
          setNavigationSteps(res.data.navigation_steps);
        }
      }
    } catch (error) {
      console.log('ETA not available:', error.response?.data?.reason);
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await api.get(`/roadside/chat/${requestId}`);
      setMessages(res.data || []);
    } catch (error) {
      // Chat might not be available yet
      console.log('Chat not available:', error.response?.data?.detail);
    }
  };

  const fetchProviderLocation = async () => {
    if (!request?.provider_id) {
      console.log('No provider_id yet, skipping location fetch');
      return;
    }
    
    try {
      const res = await api.get(`/roadside/provider/${request.provider_id}/location`);
      if (res.data?.location) {
        console.log('Provider location updated:', res.data.location);
        setProviderLocation(res.data.location);
      }
    } catch (error) {
      console.log('Provider location not available:', error.response?.data?.detail || error.message);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      await api.post('/roadside/chat', {
        request_id: requestId,
        message: newMessage.trim()
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
    setSending(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isCustomer = request?.customer_id === user?.user_id;
  const isProvider = request?.provider_id === user?.user_id;
  const canChat = ['accepted', 'en_route', 'arrived', 'in_progress', 'completed'].includes(request?.status);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-slate-400">Request not found</p>
            <Button className="mt-4" onClick={() => navigate('/roadside')}>
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
  const customerLocation = request.location ? { lat: request.location.lat, lng: request.location.lng } : null;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="roadside-tracking-page">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="font-semibold">{request.service_name}</h1>
              <p className="text-sm text-slate-400">Request #{request.id?.slice(-8)}</p>
            </div>
          </div>
          <Badge className={statusConfig.color}>
            {statusConfig.label}
          </Badge>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto p-4">
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Left Column - Map & Status */}
          <div className="space-y-4">
            {/* Live Map */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-green-400" />
                  Live Tracking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] rounded-lg overflow-hidden border border-slate-700 relative">
                  {customerLocation && (
                    <MapContainer
                      center={[customerLocation.lat, customerLocation.lng]}
                      zoom={14}
                      className="h-full w-full"
                    >
                      <MapLayerSwitch mapLayer={mapLayer} />
                      
                      {/* Customer Location */}
                      <Marker position={[customerLocation.lat, customerLocation.lng]} icon={customerIcon}>
                        <Popup>
                          <strong>Your Location</strong><br/>
                          {request.location?.address}
                        </Popup>
                      </Marker>
                      
                      {/* Provider Location */}
                      {providerLocation && (
                        <Marker position={[providerLocation.lat, providerLocation.lng]} icon={providerIcon}>
                          <Popup>
                            <strong>{request.provider_name}</strong><br/>
                            Provider Location
                          </Popup>
                        </Marker>
                      )}
                      
                      {/* Route Polyline - Driving path from provider to customer */}
                      {routeCoordinates.length > 0 && (
                        <Polyline
                          positions={routeCoordinates}
                          pathOptions={{
                            color: '#3b82f6',  // Blue color
                            weight: 4,
                            opacity: 0.8,
                            dashArray: '10, 10',  // Dashed line for route
                            lineCap: 'round',
                            lineJoin: 'round'
                          }}
                        />
                      )}
                      
                      <MapController 
                        customerLocation={customerLocation} 
                        providerLocation={providerLocation} 
                      />
                    </MapContainer>
                  )}
                  {/* Map Layer Controls */}
                  <div className="absolute top-2 right-2 z-[1000]">
                    <MapLayerButtons mapLayer={mapLayer} setMapLayer={setMapLayer} />
                  </div>
                </div>
                
                <div className="flex items-center justify-center gap-4 mt-3 text-sm flex-wrap">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-slate-400">Your Location</span>
                  </div>
                  {providerLocation && (
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <span className="text-slate-400">Provider</span>
                    </div>
                  )}
                  {routeCoordinates.length > 0 && (
                    <div className="flex items-center gap-1">
                      <div className="w-6 h-0.5 bg-blue-500 border-dashed" style={{ borderStyle: 'dashed' }} />
                      <span className="text-slate-400">Route</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* ETA Card - Shows when provider is en route */}
            {eta && eta.eta_available && ['accepted', 'en_route'].includes(request?.status) && (
              <Card className="border-blue-500/30 bg-gradient-to-r from-blue-500/10 to-purple-500/10" data-testid="eta-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-full bg-blue-500/20">
                        <Navigation className="h-6 w-6 text-blue-400 animate-pulse" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-400">Estimated Arrival</p>
                        <p className="text-2xl font-bold text-white">{eta.eta_text}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-400">Distance</p>
                      <p className="text-lg font-semibold text-slate-200">{eta.distance_text}</p>
                    </div>
                  </div>
                  {eta.arrival_time && (
                    <div className="mt-3 pt-3 border-t border-slate-700/50 flex items-center justify-between text-sm">
                      <span className="text-slate-400">Expected at</span>
                      <span className="text-white font-medium">
                        {new Date(eta.arrival_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Turn-by-Turn Navigation */}
            {navigationSteps.length > 0 && ['accepted', 'en_route'].includes(request?.status) && (
              <Card data-testid="navigation-steps">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Route className="h-5 w-5 text-green-400" />
                      Turn-by-Turn Directions
                    </CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowDirections(!showDirections)}
                    >
                      {showDirections ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </CardHeader>
                {showDirections && (
                  <CardContent className="pt-0">
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {navigationSteps.map((step, idx) => {
                        // Determine icon based on instruction type
                        const getStepIcon = (instruction) => {
                          const lower = instruction.toLowerCase();
                          if (lower.includes('left')) return CornerUpLeft;
                          if (lower.includes('right')) return CornerUpRight;
                          if (lower.includes('u-turn') || lower.includes('uturn')) return RotateCcw;
                          if (lower.includes('arrive') || lower.includes('destination')) return Flag;
                          return ArrowUp;
                        };
                        
                        const StepIcon = getStepIcon(step.instruction);
                        const isLast = idx === navigationSteps.length - 1;
                        
                        return (
                          <div 
                            key={idx} 
                            className={`flex items-start gap-3 p-2 rounded-lg ${isLast ? 'bg-green-500/10' : 'hover:bg-slate-800/50'}`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                              isLast ? 'bg-green-500/20 text-green-400' : 'bg-slate-800 text-slate-400'
                            }`}>
                              <StepIcon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${isLast ? 'text-green-400 font-medium' : 'text-slate-300'}`}>
                                {step.instruction}
                              </p>
                              {step.distance && (
                                <p className="text-xs text-slate-500 mt-0.5">
                                  {step.distance}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      {navigationSteps.length} steps • Powered by OSRM
                    </p>
                  </CardContent>
                )}
                {!showDirections && (
                  <CardContent className="pt-0">
                    <p className="text-sm text-slate-400 text-center">
                      {navigationSteps.length} navigation steps available
                    </p>
                  </CardContent>
                )}
              </Card>
            )}
            
            {/* Status Timeline */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-400" />
                  Status Updates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { status: 'pending', label: 'Request Submitted', time: request.created_at },
                    { status: 'accepted', label: 'Provider Accepted', time: request.accepted_at },
                    { status: 'en_route', label: 'Provider En Route', time: request.en_route_at },
                    { status: 'arrived', label: 'Provider Arrived', time: request.arrived_at },
                    { status: 'in_progress', label: 'Service Started', time: request.started_at },
                    { status: 'completed', label: 'Service Completed', time: request.completed_at },
                  ].map((step, idx) => {
                    const isCompleted = step.time;
                    const isCurrent = request.status === step.status;
                    
                    return (
                      <div key={step.status} className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isCompleted ? 'bg-green-500/20 text-green-400' :
                          isCurrent ? 'bg-blue-500/20 text-blue-400' :
                          'bg-slate-800 text-slate-500'
                        }`}>
                          {isCompleted ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <span className="text-xs">{idx + 1}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm ${isCompleted || isCurrent ? 'text-white' : 'text-slate-500'}`}>
                            {step.label}
                          </p>
                          {step.time && (
                            <p className="text-xs text-slate-400">
                              {new Date(step.time).toLocaleTimeString()}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            
            {/* Provider Info */}
            {request.provider_name && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <Truck className="h-6 w-6 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{request.provider_name}</p>
                      {request.provider_phone && (
                        <a 
                          href={`tel:${request.provider_phone}`}
                          className="text-sm text-blue-400 flex items-center gap-1"
                        >
                          <Phone className="h-3 w-3" />
                          {request.provider_phone}
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* Right Column - Chat */}
          <Card className="flex flex-col h-[600px]">
            <CardHeader className="pb-2 border-b border-slate-800">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-purple-400" />
                Chat
                {!canChat && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    Available after provider accepts
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="flex-1 flex flex-col p-0">
              {/* Messages Area */}
              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    {canChat ? 'No messages yet. Start the conversation!' : 'Chat will be available once provider accepts the job.'}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg) => {
                      const isMe = msg.sender_id === user?.user_id;
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] rounded-lg p-3 ${
                            isMe 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-slate-800 text-slate-100'
                          }`}>
                            {!isMe && (
                              <p className="text-xs text-slate-400 mb-1">{msg.sender_name}</p>
                            )}
                            <p className="text-sm">{msg.message}</p>
                            <p className={`text-xs mt-1 ${isMe ? 'text-blue-200' : 'text-slate-500'}`}>
                              {new Date(msg.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
              
              {/* Message Input */}
              {canChat && (
                <div className="p-4 border-t border-slate-800">
                  <div className="flex gap-2">
                    <Input
                      ref={chatInputRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type a message..."
                      disabled={sending}
                      data-testid="chat-input"
                    />
                    <Button 
                      onClick={sendMessage} 
                      disabled={sending || !newMessage.trim()}
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Vehicle Info */}
        {request.vehicle && (
          <Card className="mt-4">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Truck className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <p className="font-medium">
                    {request.vehicle.year} {request.vehicle.make} {request.vehicle.model}
                  </p>
                  <p className="text-sm text-slate-400">
                    {request.vehicle.color}
                    {request.vehicle.license_plate && ` • ${request.vehicle.license_plate}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Review Prompt for Completed Requests */}
        {request.status === 'completed' && !hasReviewed && (
          <div className="mt-4">
            <ReviewPrompt 
              request={request} 
              onReviewClick={() => setShowReviewDialog(true)} 
            />
          </div>
        )}
      </div>
      
      {/* Review Dialog - Using hook handlers */}
      <ReviewDialog
        open={showReviewDialog}
        onOpenChange={setShowReviewDialog}
        request={request}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </div>
  );
}
